<script setup lang="ts">
import { FilterListCircle, WarningTriangle } from "@iconoir/vue";
import {
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectRoot,
  SelectTrigger,
  SelectViewport,
} from "reka-ui";
import { computed } from "vue";

const { turfIds } = defineProps<{
  turfIds: Array<string>;
}>();

const selectedTurfId = defineModel<string | null>({ default: null });

const selectedTurfIdInTurfIds = computed(() => {
  if (selectedTurfId.value === null) return true;
  return turfIds.includes(selectedTurfId.value);
});

const turfLabel = (turfId: string | null): string => {
  if (turfId === null) return "Alle Häuser";
  if (turfIds.indexOf(turfId) < 0) throw new Error("turfId does not exist");
  return `Gruppe ${turfIds.indexOf(turfId) + 1}`;
};
</script>

<template>
  <div>
    <label class="visually-hidden" for="turf-select">Häuser filtern</label>
    <SelectRoot v-model="selectedTurfId">
      <SelectTrigger as-child>
        <button
          id="turf-select"
          type="button"
          class="turf-select__trigger"
          :class="{
            'filter-active': selectedTurfIdInTurfIds && selectedTurfId !== null,
            'filter-error': !selectedTurfIdInTurfIds,
          }"
        >
          <template v-if="selectedTurfIdInTurfIds">
            <FilterListCircle />
            <span>Filter: {{ turfLabel(selectedTurfId) }}</span>
          </template>
          <template v-else>
            <WarningTriangle />
            <span>Die ausgewählte Gruppe existiert nicht mehr.</span>
          </template>
        </button>
      </SelectTrigger>
      <SelectContent position="popper" class="turf-select__content">
        <SelectViewport>
          <SelectItem :value="null" class="turf-select__option">
            <SelectItemText>Alle Häuser</SelectItemText>
          </SelectItem>
          <SelectItem
            v-for="turfId in turfIds"
            :key="turfId"
            :value="turfId"
            class="turf-select__option"
          >
            <SelectItemText>{{ turfLabel(turfId) }}</SelectItemText>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectRoot>
  </div>
</template>

<style scoped>
.turf-select__trigger {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  border: solid 2px var(--color-primary);
  border-radius: var(--border-radius-input);
  background-color: var(--bg-color-input);
  padding: var(--padding-input);
  color: var(--text-color-input);
  cursor: pointer;

  &.filter-active {
    background-color: var(--color-primary);
    color: var(--text-color-on-primary);
  }

  &.filter-error {
    color: var(--text-color-error);
  }
}

:deep(.turf-select__content) {
  inline-size: var(--reka-select-trigger-width);
  max-block-size: 15rem;
  overflow-y: auto;
  margin-block-start: var(--spacing-xs);
  background-color: var(--bg-color-card);
  border-radius: var(--border-radius-input);
  box-shadow: var(--shadow);
}

:deep(.turf-select__option) {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  cursor: pointer;
  color: var(--text-color-input);

  &[data-highlighted] {
    background-color: var(--bg-color-combobox-item-highlighted);
  }
}
</style>
